import React from 'react';
import PropTypes from 'prop-types';
import { Badge } from '@opendate-app/setlist';

const OrderHeaderCard = ({ order, buyer, event, financials }) => (
  <div className="op-card op-header-card">
    <div className="op-header-grid">
      <section className="op-section">
        <h2 className="op-section-title">Buyer</h2>
        <div className="op-field">{buyer.name || '—'}</div>
        <div className="op-field op-muted">{buyer.email}</div>
        {buyer.phone && <div className="op-field op-muted">{buyer.phone}</div>}
        {buyer.fanUrl && <a className="op-link" href={buyer.fanUrl}>View fan</a>}
      </section>

      {event?.eventUrl && (
        <section className="op-section">
          <h2 className="op-section-title">Event</h2>
          <div className="op-field">{event.title}</div>
          {event.venueName && <div className="op-field op-muted">{event.venueName}</div>}
          <a className="op-link" href={event.eventUrl}>View event</a>
        </section>
      )}

      <section className="op-section">
        <h2 className="op-section-title">Order</h2>
        <div className="op-field">
          <span className={`op-badge op-badge-${order.state}`}>{order.state}</span>
          {order.chargedBack && <Badge color="error" size="sm" style={{ marginLeft: 8 }}>Charged back</Badge>}
        </div>
        <div className="op-field op-muted">{order.humanPurchasedAt}</div>
        <div className="op-field op-muted">{financials.paymentMethod}</div>
        {order.manualType && <div className="op-field op-muted">{order.manualType}</div>}
        {order.placedBy && <div className="op-field op-muted">Placed by {order.placedBy}</div>}
      </section>
    </div>

    <section className="op-totals">
      <Row label="Subtotal" value={financials.subtotal} currency={financials.currency} />
      {financials.discount > 0 && <Row label="Discount" value={-financials.discount} currency={financials.currency} />}
      <Row label="Fees" value={financials.fees} currency={financials.currency} />
      <Row label="Taxes" value={financials.taxes} currency={financials.currency} />
      <Row label="Total" value={financials.total} currency={financials.currency} strong />
      {financials.refundedAmount > 0 && <Row label="Refunded" value={-financials.refundedAmount} currency={financials.currency} />}
    </section>

    {financials.fundsBreakdown && (
      <section className="op-totals">
        <h2 className="op-section-title">Where the money goes</h2>
        {financials.fundsBreakdown.map(({ label, amount, kind }) => (
          <Row key={label} label={label} value={amount} currency={financials.currency}
            strong={kind === 'total'} detail={kind === 'detail'} />
        ))}
      </section>
    )}
  </div>
);

const Row = ({ label, value, currency, strong, detail }) => (
  <div className={`op-total-row${strong ? ' op-total-row-strong' : ''}${detail ? ' op-total-row-detail' : ''}`}>
    <span>{label}</span>
    <span>{new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(value)}</span>
  </div>
);

OrderHeaderCard.propTypes = {
  order: PropTypes.shape({
    state: PropTypes.string,
    chargedBack: PropTypes.bool,
    humanPurchasedAt: PropTypes.string,
    manualType: PropTypes.string,
    placedBy: PropTypes.string,
  }).isRequired,
  buyer: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    fanUrl: PropTypes.string,
  }).isRequired,
  event: PropTypes.shape({
    title: PropTypes.string,
    venueName: PropTypes.string,
    eventUrl: PropTypes.string,
  }),
  financials: PropTypes.shape({
    paymentMethod: PropTypes.string,
    currency: PropTypes.string,
    subtotal: PropTypes.number,
    discount: PropTypes.number,
    fees: PropTypes.number,
    taxes: PropTypes.number,
    total: PropTypes.number,
    refundedAmount: PropTypes.number,
    fundsBreakdown: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      amount: PropTypes.number,
      kind: PropTypes.string,
    })),
  }).isRequired,
};

OrderHeaderCard.defaultProps = {
  event: null,
};

Row.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  currency: PropTypes.string,
  strong: PropTypes.bool,
  detail: PropTypes.bool,
};

Row.defaultProps = {
  currency: null,
  strong: false,
  detail: false,
};

export default OrderHeaderCard;
